import CheckboxInput from "@/components/inputs/CheckboxInput";
import NumberInput from "@/components/inputs/NumberInput";
import SelectInput from "@/components/inputs/SelectInput";
import useRequest from "@/lib/request/useRequest";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { FieldSummary } from "@/types/field";
import { Imagery } from "@/types/imagery";
import {
    VirtualSISimulation,
    VirtualSISimulationResponse,
} from "@/types/virtualSI";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Divider,
    Spinner,
} from "@heroui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
    Line,
    LineChart,
    ReferenceArea,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from "recharts";
import { array, boolean, InferType, number, object, string } from "yup";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

export const Route = createFileRoute("/dashboard/home/virtual-si")({
    component: RouteComponent,
});

const SimulationData = ({
    simulation,
}: {
    simulation: VirtualSISimulation;
}) => {
    if (!simulation) return null;
    return (
        <div>
            <span className="text-xl font-semibold">Simulation Data</span>
            <div className="grid grid-cols-[1fr_1fr] gap-2">
                {[
                    ["Crop", simulation.SIMULATION.CROP, ""],
                    ["Planting Date", simulation.SIMULATION.PLANTING_DATE, ""],
                    [
                        "Planting Depth",
                        simulation.SIMULATION.PLANTING_DEPTH,
                        "cm",
                    ],
                    [
                        "Plant Population",
                        simulation.SIMULATION.PLANT_POPULATION,
                        "seeds/m2",
                    ],
                    [
                        "GDU to Silk",
                        simulation.SIMULATION.CULTIVAR.GDU_TO_SILK,
                        "",
                    ],
                    [
                        "GDU to Maturity",
                        simulation.SIMULATION.CULTIVAR.GDU_TO_MATURITY,
                        "",
                    ],
                ].map(([label, value, unit], i, arr) => (
                    <div
                        key={label}
                        className={`flex ${i < arr.length - 1 ? "border-b border-gray-100" : ""}`}
                    >
                        <div className="font-semibold flex-1 px-2 py-1.5">
                            {label}
                        </div>
                        <div className="flex-1 px-2 py-1.5">
                            {value} {unit}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const APPLICATION_TYPES = [
    { id: "BROADCAST", description: "BROADCAST" },
    { id: "BROADCAST_INCORPORATED", description: "BROADCAST_INCORPORATED" },
    { id: "BANDED_ON_SURFACE", description: "BANDED_ON_SURFACE" },
    { id: "BANDED_BENEATH_SURFACE", description: "BANDED_BENEATH_SURFACE" },
    { id: "FERTIGATION", description: "FERTIGATION" },
    { id: "FOLIAR_SPRAY", description: "FOLIAR_SPRAY" },
    { id: "DEEP_HOLE_PLACEMENT", description: "DEEP_HOLE_PLACEMENT" },
    { id: "ON_THE_SEED", description: "ON_THE_SEED" },
    { id: "INJECTED", description: "INJECTED" },
];

const schema = object({
    applications: array().of(
        object({
            date: string(),
            depth: number(),
            method: string(),
            n_kgha: number(),
            is_establishment: boolean(),
            composition: object({
                NO3_COMP: number(),
                NH4_COMP: number(),
                UREA_COMP: number(),
                N_PCT: number(),
                P_PCT: number(),
                K_PCT: number(),
                S_PCT: number(),
            }),
        }),
    ),
});

type FormType = InferType<typeof schema>;

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation("messages");
    const { formatDateMMDDYYYY } = useI18n();

    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const fieldSummaryQuery = useGetRequest<FieldSummary[]>({
        url: "/fields/summary/",
    });

    const fieldItems = useMemo(() => {
        return sortArrayByKey(
            fieldSummaryQuery.data?.map((field) => ({
                label: field.name + " - " + field.grower.name,
                value: field.id,
            })) ?? [],
            "label",
        );
    }, [fieldSummaryQuery.data]);

    const [selectedFieldId, setSelectedFieldId] = useState<
        string | number | null | undefined
    >(null);

    const [selectedVirtualSIId, setSelectedVirtualSIId] = useState<
        number | null | undefined
    >();

    const simsForFieldQuery = useGetRequest<
        {
            id: number;
            field_id: number;
            created_date: string;
        }[]
    >({
        url: "/virtual_si/summary/",
        params: {
            field_id: selectedFieldId,
        },
        enabled: !!selectedFieldId,
    });

    // Hard coded for now just to get this rolling, can always switch to a date picker later
    const [startDate, endDate] = ["2024-10-10", "2025-10-10"];

    const payloadSummaryQuery = useGetRequest<VirtualSISimulation>({
        url: "/virtual_si/payload/",
        params: {
            field_id: selectedFieldId,
            start_date: startDate,
            end_date: endDate,
        },
        enabled: !!selectedFieldId,
    });

    const activeVirtualSIQuery = useGetRequest<{
        id: number;
        field_id: number;
        bulk_request: VirtualSISimulation;
        canary_request: VirtualSISimulation;
        reference_request: VirtualSISimulation;
        bulk_response: VirtualSISimulationResponse;
        canary_response: VirtualSISimulationResponse;
        reference_response: VirtualSISimulationResponse;
        virtual_si: { date: string; si: number }[];
        days_deficient: number;
        days_sufficient: number;
        days_approaching_deficiency: number;
    }>({
        url: `/virtual_si/${selectedVirtualSIId}/`,
        enabled: !!selectedVirtualSIId,
    });

    const runSimulation = useMutateRequest<{ id: number }>({
        successMessage: t("success.simulation_complete"),
        errorMessage: t("error.failed_to_run_simulation"),
    });

    const { fields, replace } = useFieldArray({
        control: form.control,
        name: "applications",
    });

    useEffect(() => {
        if (!selectedFieldId) {
            setSelectedVirtualSIId(undefined);
            replace([]);
        }
    }, [selectedFieldId, replace]);

    useEffect(() => {
        if (
            payloadSummaryQuery.data?.MANAGEMENT.FERTILIZER &&
            !selectedVirtualSIId
        ) {
            replace(
                Object.entries(
                    payloadSummaryQuery.data.MANAGEMENT.FERTILIZER,
                ).map(([date, app]) => ({
                    date,
                    method: app.METHOD,
                    n_kgha: app.N_KGHA,
                    composition: app.COMPOSITION,
                    depth: app.DEPTH,
                    is_establishment: app.is_establishment,
                })),
            );
        } else if (activeVirtualSIQuery.data) {
            replace([
                ...Object.entries(
                    activeVirtualSIQuery.data.bulk_request.MANAGEMENT
                        .FERTILIZER,
                ).map(([date, app]) => ({
                    date,
                    method: app.METHOD,
                    n_kgha: app.N_KGHA,
                    composition: app.COMPOSITION,
                    depth: app.DEPTH,
                    is_establishment: app.is_establishment,
                })),
            ]);
        }
    }, [
        payloadSummaryQuery.data,
        selectedVirtualSIId,
        activeVirtualSIQuery.data,
        replace,
    ]);

    const handleSubmit = useCallback(
        (data: FormType) => {
            runSimulation.mutate(
                {
                    url: "/virtual_si/run_simulation/",
                    method: "PUT",
                    data: {
                        id: selectedVirtualSIId,
                        field_id: selectedFieldId,
                        bulk_request: {
                            ...(payloadSummaryQuery.data ?? {}),
                            MANAGEMENT: {
                                IRRIGATION:
                                    payloadSummaryQuery.data?.MANAGEMENT
                                        .IRRIGATION,
                                FERTILIZER: data.applications?.reduce(
                                    (acc, x) => {
                                        if (!x.date) return acc;
                                        acc[x.date] = {
                                            is_establishment:
                                                x.is_establishment,
                                            DEPTH: x.depth,
                                            METHOD: x.method,
                                            N_KGHA: x.n_kgha,
                                            COMPOSITION: {
                                                NO3_COMP:
                                                    x.composition?.NO3_COMP,
                                                NH4_COMP:
                                                    x.composition?.NH4_COMP,
                                                UREA_COMP:
                                                    x.composition?.UREA_COMP,
                                                N_PCT: x.composition?.N_PCT,
                                                P_PCT: x.composition?.P_PCT,
                                                K_PCT: x.composition?.K_PCT,
                                                S_PCT: x.composition?.S_PCT,
                                            },
                                        };
                                        return acc;
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    {} as any,
                                ),
                            },
                        },
                    } as {
                        id?: number | null;
                        field_id?: number | null;
                        bulk_request: VirtualSISimulation;
                    },
                },
                {
                    onSuccess: (resp) => {
                        setSelectedVirtualSIId(resp.id);
                    },
                },
            );
        },
        [
            payloadSummaryQuery,
            runSimulation,
            selectedFieldId,
            selectedVirtualSIId,
        ],
    );

    const bands = useMemo(() => calculateYBands("moderate"), []);

    const [min, max] = useMemo(() => {
        const values =
            activeVirtualSIQuery.data?.virtual_si.map((x) => x.si) ?? [];
        return [
            Math.max(Math.floor(Math.min(...values) / 5) * 5, 0),
            Math.min(Math.ceil(Math.max(...values) / 5) * 5, 110),
        ];
    }, [activeVirtualSIQuery.data]);

    const latestImageQuery = useGetRequest<Imagery>({
        url: `/fields/${selectedFieldId}/latest_image/`,
        enabled: !!selectedFieldId,
    });

    const generateRxMutation = useMutateRequest<{ prescription_id: number }>({
        successMessage: t("success.rx_generated_successfully"),
        errorMessage: t("error.failed_to_generate_rx"),
    });

    const navigate = useNavigate();

    const onGenerateClick = useCallback(() => {
        if (!selectedFieldId) return;
        generateRxMutation.mutate(
            {
                url: `/prescriptions/quick_rate/`,
                method: "POST",
                data: {
                    field_id: selectedFieldId,
                    image_id: latestImageQuery.data?.id,
                },
            },
            {
                onSuccess: (resp) => {
                    navigate({
                        to: "/dashboard/fields/$fieldId/rx/$rxId",
                        params: {
                            fieldId: selectedFieldId.toString(),
                            rxId: resp.prescription_id.toString(),
                        },
                    });
                },
            },
        );
    }, [
        generateRxMutation,
        latestImageQuery.data?.id,
        navigate,
        selectedFieldId,
    ]);

    return (
        <FormProvider {...form}>
            <form className="h-full overflow-y-auto">
                <div className="grid grid-cols-[20rem_1px_1fr] w-full gap-2 h-full">
                    <div className="flex flex-col gap-2 p-2">
                        <Autocomplete
                            label="Field"
                            placeholder="Select a field"
                            selectedKey={selectedFieldId}
                            onSelectionChange={(x) => {
                                setSelectedFieldId(x);
                                setSelectedVirtualSIId(undefined);
                            }}
                            labelPlacement="outside"
                            isLoading={payloadSummaryQuery.isFetching}
                        >
                            {fieldItems?.map((item) => (
                                <AutocompleteItem key={item.value}>
                                    {item.label}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                        <Divider />
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-semibold">
                                Past Simulations
                            </span>
                            <div className="flex flex-col gap-2">
                                {simsForFieldQuery.data?.map((sim) => (
                                    <div
                                        className={classNames(
                                            "hover:bg-gray-200 rounded-md cursor-pointer p-2",
                                            {
                                                "bg-gray-200":
                                                    selectedVirtualSIId ===
                                                    sim.id,
                                            },
                                        )}
                                        key={sim.id}
                                        onClick={() =>
                                            setSelectedVirtualSIId(sim.id)
                                        }
                                    >
                                        Simulation {sim.id}:{" "}
                                        {formatDateMMDDYYYY(sim.created_date)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <Divider orientation="vertical" />
                    <div className="p-2 flex flex-col gap-2">
                        {!fields?.length && (
                            <div className="flex items-center justify-center">
                                <span className="text-sm text-gray-500">
                                    Select a field or past simulation to get
                                    started
                                </span>
                            </div>
                        )}
                        {payloadSummaryQuery.isFetching && (
                            <div className="flex items-center justify-center">
                                <Spinner />
                            </div>
                        )}
                        {payloadSummaryQuery.data && (
                            <SimulationData
                                simulation={payloadSummaryQuery.data}
                            />
                        )}
                        {fields.length > 0 && (
                            <div className="p-4">
                                <h3 className="font-semibold mb-2">
                                    Fertilizer Applications
                                </h3>
                                <div className="flex flex-col gap-4">
                                    {fields.map((app, index) => (
                                        <div
                                            key={app.date}
                                            className="flex flex-row items-center gap-2 border rounded p-2"
                                        >
                                            <div className="min-w-30 font-mono text-xs">
                                                {app.date}
                                            </div>
                                            <CheckboxInput
                                                name={`applications.${index}.is_establishment`}
                                                label="Establishment"
                                                size="sm"
                                                key={`${app.id}-is_establishment`}
                                            />

                                            <SelectInput
                                                name={`applications.${index}.method`}
                                                size="sm"
                                                label="Method:"
                                                labelPlacement="outside"
                                                options={APPLICATION_TYPES}
                                                key={`${app.id}-method`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.n_kgha`}
                                                label=" N kg/ha:"
                                                size="sm"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-n_kgha`}
                                            />
                                            <NumberInput
                                                name={`applications.${index}.composition.NO3_COMP`}
                                                size="sm"
                                                min={0}
                                                readOnly
                                                label=" NO₃ (mg/kg):"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-no3_comp`}
                                            />
                                            <NumberInput
                                                name={`applications.${index}.composition.NH4_COMP`}
                                                size="sm"
                                                min={0}
                                                label=" NH₄ (mg/kg):"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-nh4_comp`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.composition.UREA_COMP`}
                                                size="sm"
                                                min={0}
                                                label=" Urea (mg/kg):"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-urea_comp`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.composition.N_PCT`}
                                                size="sm"
                                                min={0}
                                                label=" N %:"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-n_pct`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.composition.P_PCT`}
                                                size="sm"
                                                min={0}
                                                label=" P %:"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-p_pct`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.composition.K_PCT`}
                                                size="sm"
                                                min={0}
                                                label=" K %:"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-k_pct`}
                                            />

                                            <NumberInput
                                                name={`applications.${index}.composition.S_PCT`}
                                                size="sm"
                                                min={0}
                                                label=" S %:"
                                                labelPlacement="outside"
                                                classNames={{
                                                    base: "w-16",
                                                }}
                                                key={`${app.id}-s_pct`}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end mt-4 gap-3">
                                    <Button
                                        onPress={onGenerateClick}
                                        isLoading={generateRxMutation.isPending}
                                        color="primary"
                                    >
                                        Generate Rx form latest image{" "}
                                        {formatDateMMDDYYYY(
                                            latestImageQuery.data?.created_date,
                                        )}
                                    </Button>
                                    <Button
                                        onPress={() => {
                                            form.handleSubmit(handleSubmit)();
                                        }}
                                        isLoading={runSimulation.isPending}
                                        color="primary"
                                    >
                                        Run Simulation
                                    </Button>
                                </div>
                            </div>
                        )}
                        {activeVirtualSIQuery.data && (
                            <div className="p-4">
                                <h3 className="font-semibold mb-2">
                                    Simulation Results
                                </h3>
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-row gap-2 text-xs">
                                        <div className="flex flex-col gap-1 text-xs">
                                            YIELDS
                                            <div>
                                                <span className="font-semibold">
                                                    Canary:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .canary_response?.METRICS
                                                    .FINAL_YIELD ?? "N/A"}
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Bulk:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .bulk_response?.METRICS
                                                    .FINAL_YIELD ?? "N/A"}
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Reference:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .reference_response?.METRICS
                                                    .FINAL_YIELD ?? "N/A"}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1 text-xs">
                                            <div className="flex flex-col  text-xs">
                                                SI STATUS BY DAYS
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Sufficient:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .days_sufficient ?? "N/A"}
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Approaching Deficiency:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .days_approaching_deficiency ??
                                                    "N/A"}
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Deficient:
                                                </span>{" "}
                                                {activeVirtualSIQuery.data
                                                    .days_deficient ?? "N/A"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <ResponsiveContainer width="100%" height={500}>
                                    <LineChart
                                        data={
                                            activeVirtualSIQuery.data.virtual_si
                                        }
                                    >
                                        {bands?.map((band) => {
                                            return (
                                                <ReferenceArea
                                                    key={band.color}
                                                    y1={band.y0}
                                                    y2={band.y1}
                                                    fill={band.color}
                                                    ifOverflow="hidden"
                                                    fillOpacity={0.6}
                                                />
                                            );
                                        })}
                                        <XAxis dataKey="date" />
                                        <YAxis
                                            dataKey="si"
                                            domain={[min, max]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="si"
                                            stroke="#8884d8"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}
